import {useInfiniteQuery, useQueryClient} from '@tanstack/react-query';
import {useState} from 'react';
import {useParams} from 'react-router-dom';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912): nulls-last and numeric-vs-string live in the
// library, not in a per-module copy.
import {sortRecords} from '@flexinet/ui-components';

import {fetchRecords} from '../services/CustomerSubscriptionsService';
import {CustomerSubscription} from '../types';
import {useCustomerSubscriptionsFilter} from './useCustomerSubscriptionsFilter';
import {customerSubscriptionsQueryKeys} from './customerSubscriptionsQueryKeys';

// Kept here for the callers that imported it from this file.
export {useCustomerSubscriptionsFilter} from './useCustomerSubscriptionsFilter';

// Page size is a server concern fixed by the module (§9), never a user choice.
const PAGE_SIZE = 10;

// NEWEST FIRST, on `created`: the list index's sort key (record-type-created-index,
// ScanIndexForward false), so first paint agrees with the server's own order.
// `created` is nullable in the schema (AWSDateTime), but every listed row carries it:
// the create resolver always sets it, and a row without it would not be in that index.
//
// The billing-api resolver drops the `sort` argument (customer-subscriptions/list.ts
// passes only filter, limit and nextToken), so ordering is applied here.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<CustomerSubscription> = {
    columnAccessor: 'created',
    direction: 'desc',
};

// What a keyword is matched against: the id, the customer, the status, and the product
// names the Subscription column shows.
const matchesKeyword = (item: CustomerSubscription, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const productNames = (item.items?.items ?? []).map(subscriptionItem => subscriptionItem?.price?.product?.name);
    const haystack = [item.id, item.customerId, item.customer?.name, item.status, ...productNames];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

// The resolver ignores filter.status (listSubscriptions applies only customerId and the
// created range), so the status filter is ALSO applied to the loaded rows. Without this
// the default "Active" filter showed Inactive subscriptions.
const matchesFilters = (item: CustomerSubscription, filters: Record<string, string>): boolean =>
    !filters.status || item.status === filters.status;

export const useCustomerSubscriptions = (context: 'customer' | 'reports' = 'customer') => {
    const queryClient = useQueryClient();
    const {filters} = useCustomerSubscriptionsFilter();
    const {id: customerId} = useParams();

    // Component state, not a module-scoped store. The search input (TableSearchForm) and
    // the table that reads the keyword are the SAME component, RecordsTable, so there is
    // no sibling for a store to reach. SubscriptionsAutocomplete reads `items`, the
    // unfiltered loaded set, and must not inherit a table search.
    const [keyword, setKeyword] = useState('');

    // Sort is how one operator is viewing the table right now: component state.
    const [sortStatus, setSortStatus] =
        useState<DataTableSortStatus<CustomerSubscription>>(DEFAULT_SORT_STATUS);

    // The user's filters plus the customer from the URL when the table sits on a customer
    // page.
    const filter: Record<string, string> = {
        ...filters,
        ...((context === 'customer' && customerId) ? {customerId} : {}),
    };

    // Only what the server is sent is in the key. The keyword is applied client-side
    // below, so keying on it would refetch identical pages per keystroke.
    const query = useInfiniteQuery({
        queryKey: customerSubscriptionsQueryKeys.list(filter),
        queryFn: ({pageParam}: { pageParam: string | null }) => fetchRecords({
            limit: PAGE_SIZE,
            nextToken: pageParam,
            filter,
        }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        // Every mutation in this module invalidates ['customer-subscriptions'], so 30s stops
        // a remount from refetching without hiding an edit.
        staleTime: 30_000,
    });

    const items: CustomerSubscription[] =
        query.data?.pages?.flatMap(page => (page && 'items' in page && page.items) ? page.items : []) ?? [];

    const needle = keyword.trim()
                          .toLowerCase();

    // ⚠️ Client-side filter and sort cover the pages FETCHED SO FAR. Under token
    // pagination the browser cannot order or search rows it has not loaded; that is a
    // property of the model, not a bug (§9).
    const filteredItems = items.filter(item => matchesFilters(item, filters));
    const visibleItems = sortRecords(
        filteredItems.filter(item => matchesKeyword(item, needle)),
        sortStatus
    );

    const lastPage = query.data?.pages?.[query.data.pages.length - 1];

    const reloadRecords = () => queryClient.invalidateQueries({queryKey: customerSubscriptionsQueryKeys.all});

    return {
        // The loaded set BEFORE any client-side filtering: what the subscription pickers list.
        items,
        // After the status filter, before the keyword: what the footer's "N of M loaded"
        // is measured against.
        filteredItems,
        visibleItems,
        keyword,
        filters,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingMore: query.isFetchingNextPage,
        // The server's own cursor, never a stand-in: opaque to this client (§9).
        nextToken: query.hasNextPage ? (lastPage?.nextToken ?? null) : null,
        // Loads the NEXT page. Guarded on isFetchingNextPage too, so scroll-to-bottom and
        // Load More cannot start overlapping requests for the same cursor.
        fetchMoreRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        // Reload starts again from page 1. Invalidation (not refetch) so every
        // useCustomerSubscriptions instance, the table's and the Reload button's, sees it.
        reloadRecords,
        // Kept under its old name for the callers outside the table.
        fetchRecords: reloadRecords,
        handleFilterByKeyword: (newKeyword: string) => setKeyword(newKeyword),
    };
};
